/**
 * Checkout analytics emitters, as plain functions.
 *
 * These were `analyticsScribe.paywallCheckout`, `.paywallAddOnCheckout` and
 * `.trackReferralCouponAppliedToCheckout` — pure compositions of the v1 and V2
 * senders that existed as model methods only so their callers could reach them
 * through `dispatch`. The checkout mutation hooks call these instead, which is
 * what takes the Rematch leg off them; the emitted payloads are unchanged.
 */
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { trackV2 } from '@/lib/analytics/track-v2';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { SegmentActionName, SegmentObjectName } from '@/models/analytics/segment-names';
import { type Period } from '@heidi/commerce/contracts';

export function trackPaywallCheckout(paymentPeriod: Period): void {
  trackLegacyEvent({
    name: SegmentObjectName.Paywall,
    props: {
      action: SegmentActionName.PaywallCheckout,
      paymentPeriod: paymentPeriod,
    },
  });

  // V2 — checkout_started
  trackV2({
    options: { amplitude: false },
    event_name: TrackingEventName.CheckoutStarted,
    props: {
      payment_period: paymentPeriod,
    },
  });
}

export function trackPaywallAddOnCheckout(): void {
  trackLegacyEvent({
    name: SegmentObjectName.Paywall,
    props: {
      action: SegmentActionName.PaywallAddOnCheckout,
    },
  });
}

export function trackReferralCouponAppliedToCheckout(input: {
  referralId: string;
  couponId: string;
}): void {
  trackLegacyEvent({
    name: SegmentObjectName.Referral,
    props: {
      action: SegmentActionName.ReferralCouponAppliedToCheckout,
      referralId: input.referralId,
      couponId: input.couponId,
    },
  });

  // V2 — referral_completed
  trackV2({
    options: { amplitude: false },
    event_name: TrackingEventName.ReferralCompleted,
    props: {
      coupon_code: input.couponId,
      referral_id: input.referralId,
    },
  });
}
